import {
  AloftPrice,
  ManualAdjustmentKey,
  ORDER_FEATURES,
  isDefined,
  orUndefined,
  schAlertType,
  schAloftPrice,
  schPairedCostRouteItem,
} from '@aloft/toolkit-shared';

import { PropertyGridCellKey } from '../../../../components/PropertyGrid/types.ts';
import { CompAdjustments } from '../../../../redux/features/selectors/CompAdjustments.ts';
import { CompPropertyDataState, SubjectPropertyDataState } from '../../../../redux/types.ts';
import { AlertDefinition } from '../../types.ts';
import { getAlertTriggers } from '../utils/alertTriggers.ts';
import { getComparableDisplayName, getScopedAlertType } from '../utils/compTypeScoping.ts';

type SubjectData = SubjectPropertyDataState['subjectData']['editableData'];
type CompData = CompPropertyDataState['compData']['editableData'];

type PresenceCheck = {
  label: string;
  cellKey: ManualAdjustmentKey;
  subjectHasFeature: (subject: SubjectData) => boolean;
  compHasFeature: (comp: CompData) => boolean;
  getAdjustment: (compAdjustments: CompAdjustments) => AloftPrice | undefined;
};

const TARGET_CELL_TOOLTIP = 'Unexpected adjustment';

/**
 * Binary presence / absence fields
 *
 * Fires when a feature is present on BOTH properties (or absent on both), yet a non-zero
 * adjustment is recorded. This is the inverse of the missingCompAdjustment presence check.
 */
const PRESENCE_CHECKS: PresenceCheck[] = [
  {
    label: 'Pool/Spa',
    cellKey: 'poolSpa',
    subjectHasFeature: (s) =>
      ORDER_FEATURES.pool.hasFeature(s.improvements) ||
      ORDER_FEATURES.spa.hasFeature(s.improvements),
    compHasFeature: (c) =>
      ORDER_FEATURES.pool.hasFeature(c.improvements) ||
      ORDER_FEATURES.spa.hasFeature(c.improvements),
    getAdjustment: (ca) => orUndefined(schAloftPrice.parse)(ca.pool?.adjustment),
  },
  {
    label: 'Porch/Patio/Deck',
    cellKey: 'porchPatioDeck',
    subjectHasFeature: (s) =>
      ORDER_FEATURES.porch.hasFeature(s.improvements) ||
      ORDER_FEATURES.patio.hasFeature(s.improvements) ||
      ORDER_FEATURES.deck.hasFeature(s.improvements),
    compHasFeature: (c) =>
      ORDER_FEATURES.porch.hasFeature(c.improvements) ||
      ORDER_FEATURES.patio.hasFeature(c.improvements) ||
      ORDER_FEATURES.deck.hasFeature(c.improvements),
    getAdjustment: (ca) => orUndefined(schAloftPrice.parse)(ca.porchPatioDeck?.adjustment),
  },
  {
    label: 'Attic',
    cellKey: 'attic',
    subjectHasFeature: (s) => ORDER_FEATURES.attic.hasFeature(s.improvements),
    compHasFeature: (c) => ORDER_FEATURES.attic.hasFeature(c.improvements),
    getAdjustment: (ca) => orUndefined(schAloftPrice.parse)(ca.attic?.adjustment),
  },
  {
    label: 'Fireplace',
    cellKey: 'fireplace',
    subjectHasFeature: (s) => ORDER_FEATURES.fireplace.hasFeature(s.improvements),
    compHasFeature: (c) => ORDER_FEATURES.fireplace.hasFeature(c.improvements),
    getAdjustment: (ca) => orUndefined(schAloftPrice.parse)(ca.fireplace?.adjustment),
  },
  {
    label: 'Barn',
    cellKey: 'barn',
    subjectHasFeature: (s) => ORDER_FEATURES.barn.hasFeature(s.improvements),
    compHasFeature: (c) => ORDER_FEATURES.barn.hasFeature(c.improvements),
    getAdjustment: (ca) => orUndefined(schAloftPrice.parse)(ca.barn?.adjustment),
  },
  {
    label: 'ADU',
    cellKey: 'adu',
    subjectHasFeature: (s) => ORDER_FEATURES.adu.hasFeature(s.improvements),
    compHasFeature: (c) => ORDER_FEATURES.adu.hasFeature(c.improvements),
    getAdjustment: (ca) => orUndefined(schAloftPrice.parse)(ca.adu?.adjustment),
  },
  {
    label: 'Shed',
    cellKey: 'shed',
    subjectHasFeature: (s) => ORDER_FEATURES.shed.hasFeature(s.improvements),
    compHasFeature: (c) => ORDER_FEATURES.shed.hasFeature(c.improvements),
    getAdjustment: (ca) => orUndefined(schAloftPrice.parse)(ca.shed?.adjustment),
  },
  {
    label: 'Workshop',
    cellKey: 'workshop',
    subjectHasFeature: (s) => ORDER_FEATURES.workshop.hasFeature(s.improvements),
    compHasFeature: (c) => ORDER_FEATURES.workshop.hasFeature(c.improvements),
    getAdjustment: (ca) => orUndefined(schAloftPrice.parse)(ca.workshop?.adjustment),
  },
];

export const unexpectedCompAdjustment: AlertDefinition = ({
  compAdjustments,
  compSequence,
  compType,
  order,
  propertyDataEntities,
}) => {
  const subjectData = (propertyDataEntities.subject as SubjectPropertyDataState | undefined)
    ?.subjectData.editableData;

  if (!subjectData) return undefined;

  const fixRoute =
    schPairedCostRouteItem.enum[order.isInspectifyOrder ? 'adjustments-grid' : 'adjustments'];
  const triggers = getAlertTriggers({ fixRoute, order, includeUpdate: true });

  type Hit = { id: string; check: PresenceCheck; bothHave: boolean };

  const hit = compSequence
    .flatMap((id): Hit[] => {
      const compData = (propertyDataEntities[id] as CompPropertyDataState | undefined)?.compData
        .editableData;
      const compAdjustmentData = compAdjustments[id];
      if (!compData || !compAdjustmentData) return [];

      const presenceHit = PRESENCE_CHECKS.find((check) => {
        const subjectHas = check.subjectHasFeature(subjectData);
        const compHas = check.compHasFeature(compData);

        // Only fire when both have the same presence state (both have or both lack the feature)
        if (subjectHas !== compHas) return false;

        const adjustment = check.getAdjustment(compAdjustmentData);
        return isDefined(adjustment) && !adjustment.equals(AloftPrice.zero);
      });

      if (!presenceHit) return [];

      return [
        {
          id,
          check: presenceHit,
          bothHave: presenceHit.subjectHasFeature(subjectData),
        },
      ];
    })
    .at(0);

  if (!hit) return undefined;

  const { id, check, bothHave } = hit;
  const propertyName = getComparableDisplayName(id, compSequence);
  const { label, cellKey } = check;
  const targetCellKey = `${id}-${cellKey}` as PropertyGridCellKey;

  const bothVerb = bothHave ? 'have' : 'do not have';

  return {
    type: getScopedAlertType(schAlertType.enum.unexpectedCompAdjustment, compType),
    status: 'active',
    severity: 'error',
    orderType: ['saas', 'appraisify'],
    message: `${propertyName} has a ${label} adjustment, but both the Subject and ${propertyName} ${bothVerb} a ${label}.`,
    propertyID: id,
    ...(compType ? { compType } : {}),
    fixingInfo: {
      tips: `Since both the Subject and ${propertyName} ${bothVerb} a ${label}, no adjustment is expected for this feature. Remove the adjustment, or explain why it is justified.`,
      pairedCostRouteItem: fixRoute,
      fixingMethod: ['comment', 'action'],
      commentAlwaysRequired: false,
      targetCellKey,
      targetCellTooltip: TARGET_CELL_TOOLTIP,
    },
    triggers,
  };
};
